import { AuditLogsAgGridWrapper, CustomTabs, ErrorBoundary, CtyButton, CtyShowToast, checkType, VersionCompareModal, normalizeConfigurationDataForCompare, getAgentViewAuditLogStaticFilters, McpToolEditorModal, McpToolMappingControls, useMcpToolEditorState } from "@circuitry-ai/doc-ui";
import { useState, useEffect, Key, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
    useEntityResourcePermissions,
    RouteState,
    useGetAgentConfigurationVersionsMutation,
    useRollbackAgentConfigurationVersionMutation,
    useLazyGetAiWorkerConfigurationsQuery,
    useGetAiWorkerConfigurationsQuery,
    useGetAgentVersionsQuery,
    useGetAiWorkerByIdQuery,
    useTypedSelector,
    isWebAgentAgent,
    getWebAgentStartUrl,
    parseInstructionsGroups,
    resolveTenantIdFromToken,
    useGetCapabilityBindingQuery,
    useGetTenantToolsQuery,
    useUpdateMcpToolMutation,
    useUpsertActionToolMutation,
    buildAgentMcpUpsertPayload,
    normalizeMcpCapabilityName,
    resolveMcpToolMappingEnabled,
    type AgentMcpUpsertSnapshot,
    type McpToolVisibility,
    type VoiceAgentVersionListItem,
} from '@circuitry-ai/doc-data';
import { useTranslation } from "react-i18next";
import { AgentConverseLayout } from "@circuitry-ai/doc-chatadvisor";
import { VoiceAgentExecutePanel } from '@circuitry-ai/doc-voice-agent';
import AgentCreation from "./agentcreation";
import AgentConfiguration from "./agentconfiguration";
import DifyConfiguration from "./difyconfiguration";
import WebAgentInstructions from "./WebAgentInstructions";
import GatesAndWeights from "./gatesandweights";
import { DeployAgentTab } from "./DeployAgentTab";
import VoiceAgentVersionDetailsModal from './VoiceAgentVersionDetailsModal';
import { Select, SelectItem, Chip, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Tooltip, Switch } from "@nextui-org/react";
import { Icon } from "@iconify/react";

export interface AgentViewProps { }

export enum AgentViewTabs {
    DEFINITION = 'definition',
    CONFIGURATION = 'configuration',
    EXECUTE = 'execute',
    DEPLOY = 'deploy',
    TASKS = 'tasks',
    ACTIONS = 'actions',
    GATES = 'scores',
    DIFY_CONFIG = 'dify-config',
    INSTRUCTIONS = 'instructions',
    AUDIT_LOGS = 'audit-logs',
    MCP_TOOL_MAPPING = 'mcp-tool-mapping',
}

// Hoist ErrorBoundary-wrapped components to module scope
const CustomTabsWithErrorBoundary = ErrorBoundary(CustomTabs);
const AgentCreationWithErrorBoundary = ErrorBoundary(AgentCreation);
const AgentConverseLayoutWithErrorBoundary = ErrorBoundary(AgentConverseLayout);
const AgentConfigurationWithErrorBoundary = ErrorBoundary(AgentConfiguration);
const DifyConfigurationWithErrorBoundary = ErrorBoundary(DifyConfiguration);
const GatesAndWeightsWithErrorBoundary = ErrorBoundary(GatesAndWeights);
const WebAgentInstructionsWithErrorBoundary = ErrorBoundary(WebAgentInstructions);
const DeployAgentTabWithErrorBoundary = ErrorBoundary(DeployAgentTab);

export function AgentView(props: Readonly<AgentViewProps>) {

    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const tab = queryParams.get('tab');
    const selectedConfigIdFromQuery = queryParams.get('config_id');
    const selectedVersionFromQuery = queryParams.get('version');
    const selectedVersionIdFromQuery = queryParams.get('version_id');
    const { mode, aiworker_id: aiworkerIdFromPath } = useParams<{ mode: string; aiworker_id?: string; }>();
    const isCreateAgentFlow = mode === RouteState.CREATE;
    const token = useTypedSelector((state) => state.auth.token);
    // Navigation state is lost on a hard refresh; fall back to fetching the agent by the id in the URL (mirrors AdvisorView).
    const agentFromState = (location?.state as { agent?: any } | null)?.agent ?? null;
    const [agentSnapshot, setAgentSnapshot] = useState<any>(agentFromState);
    // Only hit the network when navigation state truly isn't available (e.g. a hard refresh) —
    // refetching on every normal visit would swap the agent object reference under already-mounted
    // tabs (Definition's form-seed effect, Execute's chat-reset effect), wiping in-progress state.
    const shouldFetchAgent = !isCreateAgentFlow && Boolean(aiworkerIdFromPath) && !agentFromState;
    const { data: agentByIdResponse } = useGetAiWorkerByIdQuery(aiworkerIdFromPath ?? '', {
        skip: !token || !shouldFetchAgent,
        refetchOnMountOrArgChange: true,
    });
    useEffect(() => {
        if (agentByIdResponse) {
            setAgentSnapshot(agentByIdResponse);
            return;
        }
        if (agentFromState) {
            setAgentSnapshot(agentFromState);
        }
    }, [agentByIdResponse, agentFromState]);
    const agent = agentSnapshot ?? agentByIdResponse ?? agentFromState ?? null;
    const { canUpdate } = useEntityResourcePermissions('agent', agent?.aiworker_id);

    const { data: activeAgentConfig, refetch: refetchActiveAgentConfig } = useGetAiWorkerConfigurationsQuery(
        { scope: 'tenant', pagination: { page: 1, per_page: 1 }, filters: { aiworker_id: agent?.aiworker_id ?? '', config_name: 'agent_config', status: 'active', is_active: true } } as any,
        { skip: !token || !agent?.aiworker_id }
    );
    const activeGroups = useMemo(
        () => parseInstructionsGroups(activeAgentConfig?.configurations?.[0]?.config),
        [activeAgentConfig]
    );
    const [creationTypeDraft, setCreationTypeDraft] = useState<string>('');
    const showInstructionsTab = isWebAgentAgent(agent) || creationTypeDraft === 'web_agent' || Object.values(activeGroups).some((items) => items.length > 0);
    const webAgentExecSpec = useMemo(
        () => ({ startUrl: getWebAgentStartUrl(agent), steps: activeGroups.steps ?? [], custom: activeGroups.custom ?? [] }),
        [agent, activeGroups]
    );
    const agentViewAuditLogFilters = useMemo(
        () => (agent?.aiworker_id ? getAgentViewAuditLogStaticFilters(agent.aiworker_id) : []),
        [agent?.aiworker_id]
    );
    const [queryConfiguration] = useLazyGetAiWorkerConfigurationsQuery();
    const [getAgentConfigurationVersions, { isLoading: isLoadingVersions }] = useGetAgentConfigurationVersionsMutation();
    const [rollbackAgentConfigurationVersion, { isLoading: isRollingBackVersion }] = useRollbackAgentConfigurationVersionMutation();
    const [versionData, setVersionData] = useState<any>(null);
    const [selectedVersion, setSelectedVersion] = useState<string>('');
    const [selectedVersionStatus, setSelectedVersionStatus] = useState<string>('');
    const suppressNextVersionSelectionRef = useRef(false);
    const compareIconClickRef = useRef(false);
    const pendingSelectionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [isVersionCompareOpen, setIsVersionCompareOpen] = useState<boolean>(false);
    const [compareLeftVersionId, setCompareLeftVersionId] = useState<string>('');
    const [compareRightVersionId, setCompareRightVersionId] = useState<string>('');
    const [compareAutoRunKey, setCompareAutoRunKey] = useState<number>(0);
    const [isVersionDetailsOpen, setIsVersionDetailsOpen] = useState<boolean>(false);
    const [selectedVersionForDetails, setSelectedVersionForDetails] = useState<VersionMeta | null>(null);
    const [selectedVoiceAgentVersion, setSelectedVoiceAgentVersion] = useState<string>('');
    const [selectedVoiceVersionForDetails, setSelectedVoiceVersionForDetails] = useState<VoiceAgentVersionListItem | null>(null);
    const [isVoiceVersionDetailsOpen, setIsVoiceVersionDetailsOpen] = useState(false);
    const [webAgentLiveFrames, setWebAgentLiveFrames] = useState(false);
    const [mcpToolMappingEnabled, setMcpToolMappingEnabled] = useState<boolean>(false);
    const userToggledMcpSwitchRef = useRef(false);
    const [upsertMcpTool, { isLoading: isMcpUpserting }] = useUpsertActionToolMutation();
    const [updateMcpTool, { isLoading: isMcpUpdating }] = useUpdateMcpToolMutation();
    const tenantId = useMemo(() => resolveTenantIdFromToken(token ?? ''), [token]);
    const capabilityName = normalizeMcpCapabilityName(agent?.name ?? '');
    const {
        data: capabilityBinding,
        isFetching: isBindingFetching,
        isSuccess: isBindingLoaded,
    } = useGetCapabilityBindingQuery(
        { tenantId, capabilityName },
        {
            skip: isCreateAgentFlow || !tenantId || !capabilityName,
            refetchOnMountOrArgChange: true,
        },
    );
    const existingCapability = capabilityBinding?.capability;
    const { data: tenantTools } = useGetTenantToolsQuery(tenantId, {
        skip: isCreateAgentFlow || !tenantId,
        refetchOnMountOrArgChange: true,
    });
    const matchedTenantTool = useMemo(
        () =>
            tenantTools?.find(
                (tool) => normalizeMcpCapabilityName(tool.name) === capabilityName,
            ),
        [tenantTools, capabilityName],
    );

    const voiceAssistantId = (agent?.aiworkers_attrs as Array<{ attr_name?: string; attr_value?: string }> | undefined)?.find(
        (a) => a?.attr_name === 'voice_agent_id'
    )?.attr_value ?? null;
    const isVoiceAgent = Boolean(voiceAssistantId);
    type VersionMeta = {
        version_id?: string;
        version_name?: string;
        status?: string;
        config_id?: string;
        aiworker_id?: string;
        configuration_id?: string;
        version?: number;
        is_active?: boolean;
    };

    const versionList: VersionMeta[] = useMemo(
        () => versionData?.versions ?? versionData?.data ?? [],
        [versionData]
    );

    const activeConfigurationVersion = useMemo(
        () =>
            versionList.find((version) => version.status === 'active' || version.is_active === true) ??
            versionList[0] ??
            null,
        [versionList]
    );
    const hasConfigurationVersions = versionList.length > 0;
    const activeVersionConfigurationId = activeConfigurationVersion?.configuration_id ?? '';

    const { data: voiceAgentVersionList = [], isLoading: isLoadingVoiceAgentVersions } = useGetAgentVersionsQuery(
        voiceAssistantId ?? '',
        { skip: !isVoiceAgent || !voiceAssistantId }
    );

    const sortedVoiceAgentVersions = useMemo(
        () => [...voiceAgentVersionList].sort((a, b) => b.version - a.version),
        [voiceAgentVersionList]
    );

    const parseVersionId = (versionId: string | undefined) => {
        if (!versionId || typeof versionId !== 'string') return { versionNumber: '', dateTime: '' };
        const normalized = versionId.trim();
        const compactPattern = /^v?(\d+)\.(\d{8})\.(\d{6})$/i;
        const compactMatch = normalized.match(compactPattern);
        if (compactMatch) {
            const [, versionNum, ddmmyyyy, hhmmss] = compactMatch;
            const dd = ddmmyyyy.slice(0, 2);
            const mm = ddmmyyyy.slice(2, 4);
            const yyyy = ddmmyyyy.slice(4, 8);
            const hh = hhmmss.slice(0, 2);
            const min = hhmmss.slice(2, 4);
            const date = new Date(Number(yyyy), Number(mm) - 1, Number(dd), Number(hh), Number(min), Number(hhmmss.slice(4, 6)));
            const dateTime = Number.isNaN(date.getTime())
                ? `${dd}-${mm}-${yyyy} ${hh}:${min}:${hhmmss.slice(4, 6)}`
                : `${date.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })} ${date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true })}`;
            return { versionNumber: `v${versionNum}`, dateTime };
        }
        return { versionNumber: normalized, dateTime: '' };
    };

    const getLatestVersionEntry = useCallback((versions: VersionMeta[]) => {
        if (!versions.length) return null;
        const getVersionNum = (value: string) => {
            const match = value.trim().match(/^v?(\d+)/i);
            return match ? Number.parseInt(match[1], 10) : -1;
        };
        return versions.reduce((latest, current) => {
            if (!latest) return current;
            const currentKey = current.version_id ?? current.version_name ?? '';
            const latestKey = latest.version_id ?? latest.version_name ?? '';
            return getVersionNum(currentKey) > getVersionNum(latestKey) ? current : latest;
        }, versions[0] ?? null);
    }, []);

    const fetchVersions = useCallback(async (options?: { selectLatest?: boolean }) => {
        if (!token || !agent?.aiworker_id || isCreateAgentFlow) return;
        try {
            const response = await getAgentConfigurationVersions({ aiworker_id: agent.aiworker_id }).unwrap();
            setVersionData(response);
            const versions: VersionMeta[] = response?.versions ?? response?.data ?? [];
            if (!versions.length) return;
            if (options?.selectLatest) {
                const latest = getLatestVersionEntry(versions);
                const latestKey = latest?.version_id ?? latest?.version_name ?? '';
                if (latestKey) {
                    setSelectedVersion(latestKey);
                    setSelectedVersionStatus(latest?.status ?? '');
                }
                return;
            }
            const active = versions.find((v) => v.status === 'active') ?? versions[0];
            const activeKey = active?.version_id ?? active?.version_name ?? '';
            if (activeKey) {
                setSelectedVersion((prev) => prev || activeKey);
                setSelectedVersionStatus(active?.status ?? '');
            }
        } catch {
            CtyShowToast(checkType.error, t('Failed to fetch versions'));
        }
    }, [agent?.aiworker_id, getAgentConfigurationVersions, getLatestVersionEntry, isCreateAgentFlow, t, token]);

    const baseTabItems = [
        { key: AgentViewTabs.DEFINITION, title: t('Definition'), id: 'definition-tab', isDisabled: false },
        { key: AgentViewTabs.CONFIGURATION, title: t('Configuration'), id: 'configuration-tab', isDisabled: false },
        { key: AgentViewTabs.EXECUTE, title: t('Execute'), id: 'execute-tab', isDisabled: false },
        { key: AgentViewTabs.DEPLOY, title: t('Deploy'), id: 'deploy-tab', isDisabled: false },
        { key: AgentViewTabs.AUDIT_LOGS, title: t('Audit Logs'), id: 'agent-view-audit-logs-tab', isDisabled: false },
    ];
    const tabSpecificSubText: Record<string, string> = {
        [AgentViewTabs.DEFINITION]: t('Start creating your Agent by setting up important details like its name and basic chat settings.'),
        [AgentViewTabs.CONFIGURATION]: t('Manage agent configuration settings.'),
        [AgentViewTabs.EXECUTE]: t('Test and execute your Agent workflows.'),
        [AgentViewTabs.DEPLOY]: t('Manage deployment settings for this agent.'),
        [AgentViewTabs.AUDIT_LOGS]: t('View audit history for this agent.'),
        [AgentViewTabs.MCP_TOOL_MAPPING]: t('Enable and configure MCP tool mapping for this agent.'),
    };
    const configurationTabItems = [
        { key: AgentViewTabs.ACTIONS, title: t('Actions'), id: 'actions-tab', isDisabled: false },
        { key: AgentViewTabs.GATES, title: t('Scoring'), id: 'scores-tab', isDisabled: false },
        { key: AgentViewTabs.DIFY_CONFIG, title: t('Workflow Config'), id: 'dify-config-tab', isDisabled: false },
        ...(showInstructionsTab ? [{ key: AgentViewTabs.INSTRUCTIONS, title: t('Instructions'), id: 'instructions-tab', isDisabled: false }] : []),
        { key: AgentViewTabs.MCP_TOOL_MAPPING, title: t('MCP Tool Mapping'), id: 'agent-config-mcp-tool-mapping-tab', isDisabled: false },
    ];
    const tabItems = useMemo(
        () =>
            isVoiceAgent
                ? baseTabItems.filter(
                    (item) =>
                        item.key !== AgentViewTabs.CONFIGURATION && item.key !== AgentViewTabs.AUDIT_LOGS
                )
                : baseTabItems,
        [isVoiceAgent, t]
    );

    const getSelectedTabs = (selectedTab?: string | null) => {
        if (
            isVoiceAgent &&
            (selectedTab === AgentViewTabs.CONFIGURATION ||
                selectedTab === AgentViewTabs.AUDIT_LOGS ||
                selectedTab === AgentViewTabs.ACTIONS ||
                selectedTab === AgentViewTabs.GATES ||
                selectedTab === AgentViewTabs.DIFY_CONFIG ||
                selectedTab === AgentViewTabs.INSTRUCTIONS ||
                selectedTab === AgentViewTabs.MCP_TOOL_MAPPING)
        ) {
            return { parentTab: AgentViewTabs.DEFINITION, configurationTab: AgentViewTabs.ACTIONS };
        }

        if (
            selectedTab === AgentViewTabs.ACTIONS
            || selectedTab === AgentViewTabs.GATES
            || selectedTab === AgentViewTabs.DIFY_CONFIG
            || selectedTab === AgentViewTabs.INSTRUCTIONS
            || selectedTab === AgentViewTabs.MCP_TOOL_MAPPING
        ) {
            return { parentTab: AgentViewTabs.CONFIGURATION, configurationTab: selectedTab };
        }

        if (selectedTab === AgentViewTabs.CONFIGURATION) {
            return { parentTab: AgentViewTabs.CONFIGURATION, configurationTab: AgentViewTabs.ACTIONS };
        }

        if (
            selectedTab === AgentViewTabs.EXECUTE ||
            selectedTab === AgentViewTabs.DEFINITION ||
            selectedTab === AgentViewTabs.DEPLOY ||
            selectedTab === AgentViewTabs.AUDIT_LOGS
        ) {
            return { parentTab: selectedTab, configurationTab: AgentViewTabs.ACTIONS };
        }

        return { parentTab: AgentViewTabs.DEFINITION, configurationTab: AgentViewTabs.ACTIONS };
    };

    const initialSelection = getSelectedTabs(tab);
    const [currentTab, setCurrentTab] = useState<string>(initialSelection.parentTab);
    const [currentConfigurationTab, setCurrentConfigurationTab] = useState<string>(initialSelection.configurationTab);

    useEffect(() => {
        const selectedTabs = getSelectedTabs(tab);
        setCurrentTab(selectedTabs.parentTab);
        setCurrentConfigurationTab(selectedTabs.configurationTab);
    }, [tab]);

    useEffect(() => {
        if (currentTab === AgentViewTabs.EXECUTE && token && agent?.aiworker_id && isWebAgentAgent(agent)) {
            refetchActiveAgentConfig();
        }
    }, [currentTab, token, agent, refetchActiveAgentConfig]);

    useEffect(() => {
        if (!isVoiceAgent) {
            fetchVersions();
        }
    }, [fetchVersions, isVoiceAgent]);

    useEffect(() => {
        if (!isVoiceAgent || sortedVoiceAgentVersions.length === 0) return;
        const stillExists = sortedVoiceAgentVersions.some(
            (version) => String(version.version) === selectedVoiceAgentVersion
        );
        if (!selectedVoiceAgentVersion || !stillExists) {
            setSelectedVoiceAgentVersion(String(sortedVoiceAgentVersions[0].version));
        }
    }, [isVoiceAgent, selectedVoiceAgentVersion, sortedVoiceAgentVersions]);

    useEffect(() => {
        if (!versionList.length) return;
        const selectedFromQuery = selectedVersionIdFromQuery ?? selectedVersionFromQuery ?? '';
        if (selectedFromQuery) {
            setSelectedVersion(selectedFromQuery);
            const selectedMeta = versionList.find((v) => (v.version_id ?? v.version_name) === selectedFromQuery);
            setSelectedVersionStatus(selectedMeta?.status ?? '');
            return;
        }
        const active = versionList.find((v) => v.status === 'active') ?? versionList[0];
        const fallback = active?.version_id ?? active?.version_name ?? '';
        if (fallback) {
            setSelectedVersion(fallback);
            setSelectedVersionStatus(active?.status ?? '');
        }
    }, [selectedVersionFromQuery, selectedVersionIdFromQuery, versionList]);

    useEffect(() => {
        return () => {
            if (pendingSelectionTimeoutRef.current) {
                clearTimeout(pendingSelectionTimeoutRef.current);
                pendingSelectionTimeoutRef.current = null;
            }
        };
    }, []);

    const navigateToTab = useCallback((tabKey: string, extraState?: Record<string, unknown>, extraQueryParams?: Record<string, string | null | undefined>) => {
        const nextState = { agent, ...(extraState ?? {}) };
        const nextQuery = new URLSearchParams();
        nextQuery.set('tab', tabKey);
        Object.entries(extraQueryParams ?? {}).forEach(([key, value]) => {
            if (value) nextQuery.set(key, value);
        });
        const basePath = agent?.aiworker_id ? `/agents/${mode}/${agent.aiworker_id}` : `/agents/${mode}`;
        navigate(`${basePath}?${nextQuery.toString()}`, { state: nextState });
    }, [agent, mode, navigate]);

    //in Route params add tab?
    const onTabChange = (selectedTab: Key) => {
        const parentTab = selectedTab as string;
        const nextTab = parentTab === AgentViewTabs.CONFIGURATION ? currentConfigurationTab : parentTab;
        setCurrentTab(parentTab);
        navigateToTab(nextTab, undefined, {
            config_id: selectedConfigIdFromQuery,
            version: selectedVersionFromQuery,
            version_id: selectedVersionIdFromQuery,
        });
    };

    const onConfigurationTabChange = (selectedTab: Key) => {
        const configurationTab = selectedTab as string;
        setCurrentConfigurationTab(configurationTab);
        setCurrentTab(AgentViewTabs.CONFIGURATION);
        navigateToTab(configurationTab, undefined, {
            config_id: selectedConfigIdFromQuery,
            version: selectedVersionFromQuery,
            version_id: selectedVersionIdFromQuery,
        });
    };

    const handleVersionSelection = (versionId: string) => {
        if (!versionId) return;
        setSelectedVersion(versionId);
        const selectedMeta = versionList.find((v) => (v.version_id ?? v.version_name) === versionId);
        setSelectedVersionStatus(selectedMeta?.status ?? '');
        navigateToTab(AgentViewTabs.ACTIONS, {
            config_id: selectedMeta?.config_id ?? selectedMeta?.aiworker_id ?? agent?.aiworker_id ?? null,
            version: selectedMeta?.version ?? null,
            version_id: selectedMeta?.version_id ?? versionId,
        }, {
            config_id: selectedMeta?.config_id ?? selectedMeta?.aiworker_id ?? agent?.aiworker_id ?? null,
            version: selectedMeta?.version != null ? String(selectedMeta.version) : null,
            version_id: selectedMeta?.version_id ?? versionId,
        });
    };

    const handleRollback = async () => {
        if (!agent?.aiworker_id || !selectedVersion) return;
        const selectedMeta = versionList.find((v) => (v.version_id ?? v.version_name) === selectedVersion);
        if (selectedMeta?.status === 'active') {
            CtyShowToast(checkType.warning, t('Active version cannot be rolled back'));
            return;
        }
        try {
            await rollbackAgentConfigurationVersion({
                config_id: selectedMeta?.config_id ?? selectedMeta?.aiworker_id ?? agent.aiworker_id,
                version_id: selectedMeta?.version_id ?? selectedVersion,
                config_name: 'agent_config',
            }).unwrap();
            CtyShowToast(checkType.success, t('Data rolled back successfully'));
            setSelectedVersion('');
            setSelectedVersionStatus('');
            await fetchVersions({ selectLatest: true });
            navigateToTab(currentConfigurationTab || AgentViewTabs.ACTIONS);
        } catch {
            CtyShowToast(checkType.error, t('Failed to rollback data'));
        }
    };

    const handleOpenVersionDetails = () => {
        if (isVoiceAgent) {
            const versionForDetails =
                sortedVoiceAgentVersions.find((v) => String(v.version) === selectedVoiceAgentVersion) ??
                sortedVoiceAgentVersions[0] ??
                null;
            if (!versionForDetails) {
                CtyShowToast(checkType.warning, t('No version details available.'));
                return;
            }
            setSelectedVoiceAgentVersion(String(versionForDetails.version));
            setSelectedVoiceVersionForDetails(versionForDetails);
            setIsVoiceVersionDetailsOpen(true);
            return;
        }

        let versionForDetails = versionList.find((v) => (v.version_id ?? v.version_name) === selectedVersion) ?? null;
        if (!versionForDetails) {
            versionForDetails = versionList.find((v) => v.status === 'active') ?? versionList[0] ?? null;
        }
        if (!versionForDetails) {
            CtyShowToast(checkType.warning, t('No version details available.'));
            return;
        }
        const versionKey = versionForDetails.version_id ?? versionForDetails.version_name ?? '';
        if (versionKey) {
            setSelectedVersion(versionKey);
        }
        setSelectedVersionStatus(versionForDetails.status ?? '');
        setSelectedVersionForDetails(versionForDetails);
        setIsVersionDetailsOpen(true);
    };

    const fetchAgentVersionDataForCompare = useCallback(
        async (versionMeta: VersionMeta, versionId: string) => {
            if (!versionMeta?.config_id || !versionMeta?.configuration_id) {
                throw new Error(t('Selected version metadata is missing config identifiers'));
            }
            const response = await queryConfiguration({
                scope: 'tenant',
                filters: {
                    aiworker_id: versionMeta.aiworker_id ?? agent?.aiworker_id ?? '',
                    config_name: 'agent_config',
                    config_id: versionMeta.config_id,
                    configuration_id: versionMeta.configuration_id,
                    ...(versionMeta.version != null ? { version: versionMeta.version } : {}),
                    version_id: versionMeta.version_id ?? versionId,
                },
                pagination: { page: 1, per_page: 20 },
            }).unwrap();
            return normalizeConfigurationDataForCompare(response);
        },
        [agent?.aiworker_id, queryConfiguration, t]
    );

    const handleCompareFromDropdown = (compareVersionId: string) => {
        const leftVersionId = selectedVersion;
        if (!leftVersionId) {
            CtyShowToast(checkType.warning, t('Please select a base version first'));
            return;
        }
        if (leftVersionId === compareVersionId) {
            CtyShowToast(checkType.warning, t('Please choose different versions'));
            return;
        }
        setCompareLeftVersionId(leftVersionId);
        setCompareRightVersionId(compareVersionId);
        setCompareAutoRunKey(Date.now());
        setIsVersionCompareOpen(true);
    };

    const handleCloseCompareModal = () => {
        setIsVersionCompareOpen(false);
        compareIconClickRef.current = false;
        suppressNextVersionSelectionRef.current = false;
        if (pendingSelectionTimeoutRef.current) {
            clearTimeout(pendingSelectionTimeoutRef.current);
            pendingSelectionTimeoutRef.current = null;
        }
    };

    const agentIdentity = agent?.aiworker_id ?? '';
    const isMcpToolMappingSwitchDisabled =
        isCreateAgentFlow ||
        !agent?.name?.trim() ||
        !agent?.aiworker_id ||
        isMcpUpserting ||
        isMcpUpdating;
    const hasRegisteredMcpTool = Boolean(existingCapability || matchedTenantTool?.capability_id);
    const canEditMcpToolMapping =
        canUpdate
        && !isBindingFetching
        && (hasRegisteredMcpTool || mcpToolMappingEnabled);

    useEffect(() => {
        userToggledMcpSwitchRef.current = false;
        setMcpToolMappingEnabled(false);
    }, [agentIdentity, capabilityName, isCreateAgentFlow]);

    useEffect(() => {
        if (userToggledMcpSwitchRef.current || isCreateAgentFlow) return;
        if (!capabilityName || isBindingFetching || !isBindingLoaded) return;

        setMcpToolMappingEnabled(
            resolveMcpToolMappingEnabled(capabilityBinding, matchedTenantTool),
        );
    }, [
        isCreateAgentFlow,
        capabilityName,
        capabilityBinding,
        matchedTenantTool,
        isBindingFetching,
        isBindingLoaded,
    ]);

    const getAgentMcpSnapshot = (): AgentMcpUpsertSnapshot | null => {
        const aiworkerId = agent?.aiworker_id;
        const name = agent?.name?.trim();
        if (!aiworkerId || !name) {
            return null;
        }

        return {
            aiworkerId,
            name,
            description: agent?.description ?? agent?.short_description ?? '',
        };
    };

    const {
        isOpen: isEnableMcpModalOpen,
        isUpdateMode: isMcpUpdateMode,
        enableAfterUpdate: enableAfterMcpUpdate,
        defaults: mcpModalDefaults,
        open: openMcpModal,
        close: closeMcpModal,
        onOpenChange: setIsEnableMcpModalOpen,
    } = useMcpToolEditorState({
        capability: existingCapability,
        capabilityBinding,
        tenantTool: matchedTenantTool,
        getFallbackDescription: () => getAgentMcpSnapshot()?.description ?? '',
    });

    const syncAgentMcpToolMapping = async (
        snapshot: AgentMcpUpsertSnapshot,
        enabled: boolean,
        overrides?: {
            description?: string;
            visibility?: McpToolVisibility;
        },
    ) => {
        if (!tenantId) {
            CtyShowToast(checkType.error, t('Unable to update MCP tool mapping. Tenant context is missing.'));
            return;
        }

        try {
            const mcpPayload = buildAgentMcpUpsertPayload({
                ...snapshot,
                tenantId,
                mcpToolMappingEnabled: enabled,
                mcpDescription: overrides?.description,
                mcpVisibility: overrides?.visibility,
            });
            await upsertMcpTool({ tenantId, ...mcpPayload }).unwrap();
            userToggledMcpSwitchRef.current = true;
            setMcpToolMappingEnabled(enabled);
            CtyShowToast(
                checkType.success,
                enabled ? t('MCP tool mapping enabled') : t('MCP tool mapping disabled'),
            );
        } catch (mcpError) {
            CtyShowToast(
                checkType.error,
                (mcpError as Error)?.message ?? t('Failed to sync MCP tool mapping'),
            );
            throw mcpError;
        }
    };

    const handleMcpSwitchChange = async (value: boolean) => {
        const snapshot = getAgentMcpSnapshot();

        if (!snapshot) {
            CtyShowToast(
                checkType.error,
                t('Unable to update MCP tool mapping. Save the agent first.'),
            );
            return;
        }

        if (value) {
            openMcpModal(Boolean(existingCapability), Boolean(existingCapability));
            return;
        }

        await syncAgentMcpToolMapping(snapshot, false);
    };

    const handleEnableMcpConfirm = async (
        description: string,
        visibility: McpToolVisibility,
    ) => {
        const snapshot = getAgentMcpSnapshot();

        if (!tenantId || !snapshot) {
            CtyShowToast(
                checkType.error,
                t('Unable to update MCP tool mapping. Save the agent first.'),
            );
            return;
        }

        try {
            if (isMcpUpdateMode && existingCapability) {
                await updateMcpTool({
                    tenantId,
                    capability_name: existingCapability.name,
                    description,
                    visibility,
                    parameters: existingCapability.parameters ?? [],
                }).unwrap();

                if (enableAfterMcpUpdate && !mcpToolMappingEnabled) {
                    await syncAgentMcpToolMapping(snapshot, true, {
                        description,
                        visibility,
                    });
                } else {
                    CtyShowToast(checkType.success, t('MCP tool updated successfully'));
                }

                closeMcpModal();
                return;
            }

            await syncAgentMcpToolMapping(snapshot, true, {
                description,
                visibility,
            });
            closeMcpModal();
        } catch (mcpError) {
            if (isMcpUpdateMode) {
                CtyShowToast(
                    checkType.error,
                    (mcpError as Error)?.message ?? t('Failed to update MCP tool'),
                );
            }
        }
    };

    const renderConfigurationTab = (agent: unknown) => {
        const selectedVersionNumber = selectedVersionFromQuery ? Number(selectedVersionFromQuery) : undefined;
        const parsedVersion = Number.isNaN(selectedVersionNumber) ? selectedVersionFromQuery ?? undefined : selectedVersionNumber;
        const handleVersionUpdated = async () => {
            await fetchVersions({ selectLatest: true });
            navigateToTab(currentConfigurationTab || AgentViewTabs.ACTIONS);
        };
        switch (currentConfigurationTab) {
            case AgentViewTabs.ACTIONS:
                return (
                    <AgentConfigurationWithErrorBoundary
                        agent={agent}
                        configId={selectedConfigIdFromQuery ?? undefined}
                        configVersion={parsedVersion}
                        configVersionId={selectedVersionIdFromQuery ?? undefined}
                        hasConfigurationVersions={hasConfigurationVersions}
                        activeVersionConfigurationId={activeVersionConfigurationId}
                        onVersionUpdated={handleVersionUpdated}
                    />
                );
            case AgentViewTabs.GATES:
                return (
                    <GatesAndWeightsWithErrorBoundary
                        agent={agent}
                        configId={selectedConfigIdFromQuery ?? undefined}
                        configVersion={parsedVersion}
                        configVersionId={selectedVersionIdFromQuery ?? undefined}
                        hasConfigurationVersions={hasConfigurationVersions}
                        activeVersionConfigurationId={activeVersionConfigurationId}
                        onVersionUpdated={handleVersionUpdated}
                    />
                );
            case AgentViewTabs.DIFY_CONFIG:
                return (
                    <DifyConfigurationWithErrorBoundary
                        agent={agent}
                        configId={selectedConfigIdFromQuery ?? undefined}
                        configVersion={parsedVersion}
                        configVersionId={selectedVersionIdFromQuery ?? undefined}
                        hasConfigurationVersions={hasConfigurationVersions}
                        activeVersionConfigurationId={activeVersionConfigurationId}
                        onVersionUpdated={handleVersionUpdated}
                    />
                );
            case AgentViewTabs.INSTRUCTIONS:
                return (
                    <WebAgentInstructionsWithErrorBoundary
                        agent={agent}
                        configId={selectedConfigIdFromQuery ?? undefined}
                        configVersion={parsedVersion}
                        configVersionId={selectedVersionIdFromQuery ?? undefined}
                        onVersionUpdated={handleVersionUpdated}
                    />
                );
            case AgentViewTabs.MCP_TOOL_MAPPING:
                return (
                    <div className="h-full overflow-y-auto">
                        <div className="flex items-center gap-2">
                            <McpToolMappingControls
                                isEnabled={mcpToolMappingEnabled}
                                isSwitchDisabled={isMcpToolMappingSwitchDisabled}
                                canEdit={canEditMcpToolMapping}
                                isLoading={isMcpUpserting || isMcpUpdating}
                                onEdit={() => openMcpModal(true)}
                                onSwitchChange={handleMcpSwitchChange}
                            />
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    const renderSelectedTab = () => {
        const { customInput, autoExecute } = (location?.state as { customInput?: string; autoExecute?: boolean } | null) ?? {};

        switch (currentTab) {
            case AgentViewTabs.DEFINITION:
                return <AgentCreationWithErrorBoundary agent={agent} />;
            case AgentViewTabs.EXECUTE: {
                if (isVoiceAgent && voiceAssistantId) {
                    return (
                        <VoiceAgentExecutePanel
                            assistantId={voiceAssistantId}
                            assistantName={agent?.name ?? null}
                        />
                    );
                }
                return <AgentConverseLayoutWithErrorBoundary
                    customAgent={agent}
                    customInput={customInput}
                    autoExecute={autoExecute}
                    webAgentSpec={isWebAgentAgent(agent) ? webAgentExecSpec : undefined}
                    liveFrames={webAgentLiveFrames}
                />;
            }
            case AgentViewTabs.CONFIGURATION:
                return (
                    <div className="flex h-full min-h-0 flex-col">
                        <div className="shrink-0">
                            <CustomTabsWithErrorBoundary
                                tabs={configurationTabItems}
                                selectedKey={currentConfigurationTab}
                                onTabChange={onConfigurationTabChange}
                            />
                        </div>
                        <div className="flex-1 min-h-0 overflow-hidden">{renderConfigurationTab(agent)}</div>
                    </div>
                );
            case AgentViewTabs.DEPLOY:
                return (
                    <DeployAgentTabWithErrorBoundary
                        agent={agent}
                        onVisibilityUpdated={(isPublic, metadata) =>
                            setAgentSnapshot((prev: any) =>
                                prev ? { ...prev, is_enabled: isPublic, metadata: metadata ?? prev.metadata } : prev)
                        }
                    />
                );
            case AgentViewTabs.AUDIT_LOGS:
                return agent?.aiworker_id ? (
                    <div>
                        <AuditLogsAgGridWrapper staticFilters={agentViewAuditLogFilters} />
                    </div>
                ) : null;
            default:
                return null;
        }
    };

    let disabledKeys: AgentViewTabs[];
    if (isCreateAgentFlow) {
        disabledKeys = isVoiceAgent
            ? [AgentViewTabs.EXECUTE, AgentViewTabs.DEPLOY]
            : [AgentViewTabs.EXECUTE, AgentViewTabs.AUDIT_LOGS, AgentViewTabs.DEPLOY];
    } else if (canUpdate) {
        disabledKeys = [];
    } else {
        disabledKeys = isVoiceAgent
            ? [AgentViewTabs.DEFINITION, AgentViewTabs.DEPLOY]
            : [AgentViewTabs.DEFINITION, AgentViewTabs.CONFIGURATION, AgentViewTabs.DEPLOY];
    }
    const keepDefinitionMounted = !disabledKeys.includes(AgentViewTabs.DEFINITION);

    useEffect(() => {
        // Preserve explicit URL-driven tab navigation (including nested configuration tabs).
        if (tab) {
            return;
        }

        if (!isCreateAgentFlow && disabledKeys.includes(currentTab as AgentViewTabs)) {
            onTabChange(AgentViewTabs.EXECUTE);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disabledKeys, currentTab, isCreateAgentFlow, tab]);

    return (
        <div className={`flex h-full min-h-0 flex-col ${currentTab === AgentViewTabs.CONFIGURATION ? 'gap-0' : 'gap-2'}`}>
            <div className="relative flex items-center justify-between gap-3">
                <h1 className="text-xl font-bold">
                    {isCreateAgentFlow ? t('Create Agent') : (agent?.name ?? t('Agent'))}
                </h1>
                {!isCreateAgentFlow && (
                    <div className="flex items-center gap-2">
                        {isVoiceAgent ? (
                            <>
                                <CtyButton variant="bordered" radius="sm" size="md" onPress={handleOpenVersionDetails}>
                                    {t('Details')}
                                </CtyButton>
                                <div className="min-w-[180px]">
                                    <Select
                                        placeholder={t('Choose a version')}
                                        variant="bordered"
                                        radius="sm"
                                        aria-label="voice-agent-version-selection"
                                        selectedKeys={selectedVoiceAgentVersion ? [selectedVoiceAgentVersion] : []}
                                        isLoading={isLoadingVoiceAgentVersions}
                                        size="md"
                                        onSelectionChange={(keys) => {
                                            if (keys === 'all') return;
                                            const selectedKey = Array.from(keys)[0] as string;
                                            if (selectedKey) setSelectedVoiceAgentVersion(selectedKey);
                                        }}
                                    >
                                        {sortedVoiceAgentVersions.map((item, index) => (
                                            <SelectItem
                                                key={String(item.version)}
                                                value={String(item.version)}
                                                textValue={
                                                    index === 0
                                                        ? `${t('Version {{n}}', { n: item.version })} ${t('Latest version')}`
                                                        : t('Version {{n}}', { n: item.version })
                                                }
                                            >
                                                <div className="flex items-center gap-2">
                                                    <span>{t('Version {{n}}', { n: item.version })}</span>
                                                    {index === 0 ? (
                                                        <span className="text-xs font-normal text-default-400">
                                                            {t('Latest version')}
                                                        </span>
                                                    ) : null}
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </Select>
                                </div>
                            </>
                        ) : (
                            <>
                                {selectedVersion && selectedVersionStatus ? (
                                    <Chip size="sm" variant="flat" color={selectedVersionStatus === 'active' ? 'success' : 'default'}>
                                        {t(selectedVersionStatus)}
                                    </Chip>
                                ) : null}
                                {selectedVersion && selectedVersionStatus !== 'active' ? (
                                    <CtyButton
                                        variant="bordered"
                                        radius="sm"
                                        color="primary"
                                        size="md"
                                        onPress={handleRollback}
                                        isDisabled={!canUpdate}
                                        isLoading={isRollingBackVersion}
                                    >
                                        {t('Roll back')}
                                    </CtyButton>
                                ) : null}
                                <CtyButton variant="bordered" radius="sm" size="md" onPress={handleOpenVersionDetails}>
                                    {t('Details')}
                                </CtyButton>
                                <div className="min-w-[260px]">
                                    <Select
                                        placeholder={t('Choose a version')}
                                        variant="bordered"
                                        radius="sm"
                                        aria-label="agent-version-selection"
                                        selectedKeys={selectedVersion ? [selectedVersion] : []}
                                        isLoading={isLoadingVersions}
                                        size="md"
                                        onSelectionChange={(keys) => {
                                            if (keys === 'all') return;
                                            const selectedKey = Array.from(keys)[0] as string;
                                            if (!selectedKey) return;

                                            if (pendingSelectionTimeoutRef.current) {
                                                clearTimeout(pendingSelectionTimeoutRef.current);
                                                pendingSelectionTimeoutRef.current = null;
                                            }

                                            pendingSelectionTimeoutRef.current = setTimeout(() => {
                                                if (compareIconClickRef.current) {
                                                    compareIconClickRef.current = false;
                                                    return;
                                                }
                                                if (suppressNextVersionSelectionRef.current) {
                                                    suppressNextVersionSelectionRef.current = false;
                                                    return;
                                                }
                                                handleVersionSelection(selectedKey);
                                            }, 120);
                                        }}
                                        renderValue={() => {
                                            const selected = versionList.find((v) => (v.version_id ?? v.version_name ?? '') === selectedVersion);
                                            if (!selected) return null;
                                            const { versionNumber, dateTime } = parseVersionId(selected.version_id ?? selected.version_name);
                                            return (
                                                <span className="text-sm text-foreground-500">
                                                    {versionNumber}
                                                    {dateTime ? ` ${dateTime}` : ''}
                                                </span>
                                            );
                                        }}
                                    >
                                        {versionList.map((item) => {
                                            const versionValue = item.version_id ?? item.version_name ?? '';
                                            const parsed = parseVersionId(versionValue);
                                            const line1Base = parsed.versionNumber || versionValue;
                                            const line1 = parsed.dateTime ? `${line1Base}: ${parsed.dateTime}` : line1Base;
                                            const isActive = item.status === 'active';
                                            return (
                                                <SelectItem key={versionValue} value={versionValue} textValue={line1}>
                                                    <div className="flex items-center justify-between gap-2 w-full">
                                                        <div className="flex flex-col min-w-0">
                                                            <Tooltip content={line1} placement="top" showArrow>
                                                                <span className="text-sm font-medium truncate cursor-default">{line1}</span>
                                                            </Tooltip>
                                                        </div>
                                                        <div className="inline-flex items-center gap-1.5 shrink-0">
                                                            {isActive ? (
                                                                <span
                                                                    className="inline-block w-2 h-2 rounded-full bg-success"
                                                                    aria-label={t('Active')}
                                                                    title={t('Active')}
                                                                />
                                                            ) : null}
                                                            <button
                                                                type="button"
                                                                className="inline-flex items-center justify-center w-6 h-6 rounded hover:bg-default-100 text-default-500"
                                                                aria-label={t('Compare')}
                                                                onMouseDown={(event) => {
                                                                    event.preventDefault();
                                                                    event.stopPropagation();
                                                                    compareIconClickRef.current = true;
                                                                    suppressNextVersionSelectionRef.current = true;
                                                                }}
                                                                onClick={(event) => {
                                                                    event.preventDefault();
                                                                    event.stopPropagation();
                                                                    compareIconClickRef.current = true;
                                                                    setTimeout(() => {
                                                                        compareIconClickRef.current = false;
                                                                    }, 200);
                                                                    suppressNextVersionSelectionRef.current = true;
                                                                    setTimeout(() => {
                                                                        suppressNextVersionSelectionRef.current = false;
                                                                    }, 250);
                                                                    handleCompareFromDropdown(versionValue);
                                                                }}
                                                            >
                                                                <Icon icon="teenyicons:git-compare-outline" width={16} height={16} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </SelectItem>
                                            );
                                        })}
                                    </Select>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
            {!isVoiceAgent && (
                <div className="text-base font-normal">
                    {currentTab === AgentViewTabs.CONFIGURATION
                        ? (tabSpecificSubText[currentConfigurationTab] ?? tabSpecificSubText[AgentViewTabs.CONFIGURATION])
                        : (tabSpecificSubText[currentTab] ?? t('Manage agent configuration settings.'))}
                </div>
            )}
            <div className="shrink-0 flex items-center justify-between gap-3">
                <CustomTabsWithErrorBoundary
                    tabs={tabItems}
                    selectedKey={currentTab}
                    onTabChange={onTabChange}
                    disabledKeys={disabledKeys}
                />
                {currentTab === AgentViewTabs.EXECUTE && isWebAgentAgent(agent) && (
                    <div className="flex items-center gap-2 shrink-0">
                        <Tooltip content={t('Capture a screenshot after each step to watch the page live. Adds time per step.')} placement="left">
                            <span className="flex items-center gap-1 text-xs text-default-500">
                                <Icon icon="mdi:eye-outline" width={16} />
                                {t('Live step preview')}
                            </span>
                        </Tooltip>
                        <Switch size="sm" isSelected={webAgentLiveFrames} onValueChange={setWebAgentLiveFrames} aria-label={t('Toggle live step preview')} />
                    </div>
                )}
            </div>
            <div className="flex-1 min-h-0 overflow-hidden">
                {keepDefinitionMounted && (
                    <div className={currentTab === AgentViewTabs.DEFINITION ? 'h-full' : 'hidden'}>
                        <AgentCreationWithErrorBoundary agent={agent} onCreationTypeChange={setCreationTypeDraft} />
                    </div>
                )}
                {!(keepDefinitionMounted && currentTab === AgentViewTabs.DEFINITION) && renderSelectedTab()}
            </div>
            <VersionCompareModal
                isOpen={isVersionCompareOpen}
                onClose={handleCloseCompareModal}
                versions={versionList}
                initialLeftVersionId={compareLeftVersionId}
                initialRightVersionId={compareRightVersionId}
                autoRunKey={compareAutoRunKey}
                fetchVersionData={fetchAgentVersionDataForCompare}
                getNodeDisplayName={(nodeName) => {
                    if (nodeName === 'bot_data') return t('Actions');
                    if (nodeName === 'workflow_data') return t('Scoring');
                    if (nodeName === 'dify_config') return t('Workflow Config');
                    return nodeName;
                }}
            />
            <VoiceAgentVersionDetailsModal
                assistantId={voiceAssistantId ?? ''}
                isOpen={isVoiceVersionDetailsOpen}
                onOpenChange={setIsVoiceVersionDetailsOpen}
                versionMeta={selectedVoiceVersionForDetails}
            />
            <Modal isOpen={isVersionDetailsOpen} onOpenChange={setIsVersionDetailsOpen} placement="center" backdrop="blur">
                <ModalContent>
                    {(onClose) => (
                        <>
                            <ModalHeader>{t('Version Details')}</ModalHeader>
                            <ModalBody>
                                <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                                    <span className="text-default-500">{t('Version')}</span>
                                    <span className="font-medium">
                                        {selectedVersionForDetails?.version_id ?? selectedVersionForDetails?.version_name ?? '-'}
                                    </span>
                                    <span className="text-default-500">{t('Status')}</span>
                                    <span className="font-medium">{selectedVersionForDetails?.status ? t(selectedVersionForDetails.status) : '-'}</span>
                                    <span className="text-default-500">{t('Updated By')}</span>
                                    <span className="font-medium">
                                        {(selectedVersionForDetails as any)?.updated_by_email ?? (selectedVersionForDetails as any)?.updated_by ?? '-'}
                                    </span>
                                </div>
                            </ModalBody>
                            <ModalFooter>
                                <CtyButton variant="light" onPress={onClose}>
                                    {t('Close')}
                                </CtyButton>
                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>
            <McpToolEditorModal
                isOpen={isEnableMcpModalOpen}
                onOpenChange={setIsEnableMcpModalOpen}
                defaultDescription={mcpModalDefaults.description}
                defaultVisibility={mcpModalDefaults.visibility}
                descriptionHelpText="This description is shown to the LLM when the agent is used as an MCP tool."
                isUpdateMode={isMcpUpdateMode}
                isLoading={isMcpUpserting || isMcpUpdating}
                onConfirm={({ description, visibility }) => {
                    void handleEnableMcpConfirm(description, visibility);
                }}
            />
        </div>
    );

}

export default AgentView;